import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { getPathname, requestYieldCallback, stripShopifyInternalParams, stripShopifyInternalParamsFromUrl } from '@theme/utilities';
import { navigateToSiblingHref, resolveSiblingHref } from './color-group-navigation.js';

/** @typedef {HTMLElement & { showAll?: () => void }} OverflowListElement */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  #showAllColorSiblingSwatchesInQuickView() {
    if (!this.closest('#quick-view-modal')) return;

    /** @type {NodeListOf<OverflowListElement>} */
    const overflowLists = /** @type {any} */ (
      this.querySelectorAll('fieldset[data-option-group="color_siblings"] overflow-list')
    );

    overflowLists.forEach((overflowList) => {
      const showAll = overflowList.showAll;
      if (typeof showAll !== 'function') return;

      try {
        showAll.call(overflowList);
      } catch (e) {}
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    // In Quick View, do not collapse color swatches behind an overflow "More" button.
    if (this.closest('#quick-view-modal')) {
      requestAnimationFrame(() => this.#showAllColorSiblingSwatchesInQuickView());
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Sync Color label for color sibling inputs across contexts
    try {
      if (selectedOption instanceof HTMLElement && selectedOption.dataset.optionGroup === 'color_siblings') {
        const fieldset = selectedOption.closest('fieldset[data-option-group="color_siblings"]');
        const labelSpan =
          fieldset?.querySelector('[data-color-sibling-current]') ||
          fieldset?.querySelector('.variant-option__swatch-value');
        const newLabel = /** @type {HTMLInputElement|HTMLOptionElement} */ (selectedOption).value?.toString?.() || '';
        if (labelSpan && typeof newLabel === 'string') {
          labelSpan.textContent = newLabel;
        }
      }
    } catch (e) { }

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    if (selectedOption.dataset.optionGroup === 'color_siblings') {
      const href = resolveSiblingHref(selectedOption);
      const currentPath = getPathname(this.dataset.productUrl || window.location.pathname);
      const targetPath = href ? getPathname(href) : '';

      if (href && targetPath && targetPath !== currentPath) {
        navigateToSiblingHref(href);
      }

      return;
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentPath = getPathname(this.dataset.productUrl || '');
    const connectedUrl = stripShopifyInternalParams(selectedOption.dataset.connectedProductUrl || '');
    const connectedPath = getPathname(connectedUrl);
    const loadsNewProduct = isOnProductPage && !!connectedPath && connectedPath !== currentPath;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = stripShopifyInternalParamsFromUrl(new URL(window.location.href));

	    const variantId = selectedOption.dataset.variantId || null;

	    if (isOnProductPage) {
	      const shouldWriteVariantParam =
	        selectedOption.dataset.optionGroup !== 'color_siblings' && !!event.target.closest('[data-option-role="size"]');
	      if (variantId && shouldWriteVariantParam) {
	        url.searchParams.set('variant', variantId);
	      } else {
	        url.searchParams.delete('variant');
	      }
	    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = connectedPath;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    /** @type {string} */
    let productUrl =
      selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl || '';
    productUrl = stripShopifyInternalParams(productUrl);
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const isColorSiblingOption = selectedOption.dataset.optionGroup === 'color_siblings';

    if (!isColorSiblingOption) {
      if (this.selectedOptionsValues.length && !source) {
        params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      } else if (source === 'product-card') {
        if (this.selectedOptionsValues.length) {
          params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
        } else {
          const optionValueId = selectedOption.dataset.optionValueId;
          if (!optionValueId) throw new Error('No option value ID found');
          params.push(`option_values=${optionValueId}`);
        }
      }
    }

    const queryString = params.filter(Boolean).join('&');

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      const url = getPathname(productUrl);
      const quickAddUrl = `${url}?section_id=section-rendering-product-card`;
      return queryString ? `${quickAddUrl}&${queryString}` : quickAddUrl;
    }



    if (!queryString) {
      return productUrl;
    }

    try {
      const url = stripShopifyInternalParamsFromUrl(new URL(productUrl, window.location.origin));
      for (const [key, value] of new URLSearchParams(queryString).entries()) {
        url.searchParams.set(key, value);
      }
      const search = url.searchParams.toString();
      return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    } catch (_) {
      return productUrl.includes('?') ? `${productUrl}&${queryString}` : `${productUrl}?${queryString}`;
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          const main = document.querySelector('main');
          if (main) {
            main.style.transition = 'opacity 0.05s ease-out';
            main.style.opacity = '0';

            await new Promise((resolve) => setTimeout(resolve, 50));

            this.updateMain(html);

            window.scrollTo({ top: 0, behavior: 'instant' });

            const slideshow = /** @type {any} */ (main.querySelector('slideshow-component'));
            if (slideshow && typeof slideshow.select === 'function') {
              slideshow.select(0, undefined, { animate: false });
            }

            requestAnimationFrame(() => {
              main.style.opacity = '1';
              setTimeout(() => {
                main.style.transition = '';
              }, 50);
            });
          } else {
            this.updateMain(html);
          }
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // After morphing, ensure color sibling label reflects checked input
    try {
      const fieldset = this.querySelector('fieldset[data-option-group="color_siblings"]');
      const checked = fieldset?.querySelector('input[data-option-group="color_siblings"]:checked');
      const labelSpan =
        fieldset?.querySelector('[data-color-sibling-current]') ||
        fieldset?.querySelector('.variant-option__swatch-value');
      if (checked && labelSpan) {
        labelSpan.textContent = /** @type {HTMLInputElement} */ (checked).value?.toString?.() || labelSpan.textContent;
      }
    } catch (e) { }

    // Re-initialize any newly injected size chart triggers after morph.
    try {
      if (typeof window.initSizeCharts === 'function') {
        window.initSizeCharts(this);
      }
      this.dispatchEvent(new CustomEvent('size-chart:refresh', { bubbles: true }));
      this.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    } catch (e) { }

    // The morph can re-introduce overflow-lists, so re-apply the Quick View behavior.
    this.#showAllColorSiblingSwatchesInQuickView();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions
      .filter((option) => option instanceof HTMLElement && option.dataset.optionGroup !== 'color_siblings')
      .map((option) => {
        if (!(option instanceof HTMLElement)) {
          throw new Error('Invalid option element');
        }

        const optionValueId = option.dataset.optionValueId;

        if (!optionValueId) {
          throw new Error('No option value ID found');
        }

        return optionValueId;
      });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
